import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames/bind';
import { Panel } from 'components/Panel';
import { i18nKeys, useTranslation } from 'locales';
import Integrations, {
  ExactForm,
  type ExactFormFields,
  HorusForm,
  type HorusFormFields,
  Integration,
  isEnabledIntegration,
  TeamleaderForm,
  type TeamleaderFormFields,
  YukiForm,
  type YukiFormFields,
} from 'shared/components/Integrations';
import { useLoadCompanyConfiguration } from 'shared/hooks/use-load-company-configuration';
import { Button } from 'shared/io';
import { onBoardingActions } from 'store/onBoarding/onBoarding.actions';
import { DialogShowId, DialogShowSize, dialogHide, showDialog } from 'store/view/view.actions';

import type { StepProps } from '../OnBoarding.types';

import styleIdentifiers from './Integrations.module.scss';

const styles = classNames.bind(styleIdentifiers);

export default function IntegrationsOnBoarding({ onValidStep }: StepProps) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { refetchCompany } = useLoadCompanyConfiguration();

  const callback = async () => {
    const { error, isError, isSuccess } = await refetchCompany();

    dialogHide(DialogShowId.CUSTOM);
    client.invalidateQueries({ queryKey: ['profile'] });

    if (isSuccess) onValidStep();
    else if (isError) {
      onValidStep(error.response?.data.next_step);
    }
  };

  const skipStep = () => {
    onBoardingActions.yuki({
      callback,
      data: {
        skip: true,
      },
    });
  };

  const yukiSubmit = (data: YukiFormFields) => {
    onBoardingActions.yuki({
      callback,
      data,
    });
  };

  const horusSubmit = (data: HorusFormFields) => {
    onBoardingActions.horus({
      callback,
      data,
    });
  };

  const teamleaderSubmit = (data: TeamleaderFormFields) => {
    onBoardingActions.teamleader({
      callback,
      data,
    });
  };

  const exactSubmit = (data: ExactFormFields) => {
    onBoardingActions.exact({
      callback,
      data,
    });
  };

  const integrationsProps = {
    onboarding: true,
  };

  const integrations = {
    ...(isEnabledIntegration(Integration.HORUS)
      ? {
          [Integration.HORUS]: <HorusForm {...integrationsProps} onSubmit={horusSubmit} />,
        }
      : undefined),
    ...(isEnabledIntegration(Integration.YUKI)
      ? {
          [Integration.YUKI]: <YukiForm {...integrationsProps} onSubmit={yukiSubmit} />,
        }
      : undefined),
    ...(isEnabledIntegration(Integration.TEAMLEADER)
      ? {
          [Integration.TEAMLEADER]: (
            <TeamleaderForm {...integrationsProps} onSubmit={teamleaderSubmit} />
          ),
        }
      : undefined),
    ...(isEnabledIntegration(Integration.EXACT)
      ? {
          [Integration.EXACT]: <ExactForm {...integrationsProps} onSubmit={exactSubmit} />,
        }
      : undefined),
  };

  const onIntegrationSelect = (title: string) => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.MEDIUM,
      keepMountOnExit: true,
      title,
      children: integrations[title as keyof typeof integrations],
    });
  };

  return (
    <div className={styles('integrations')}>
      <div className={styles('steps')}>
        <Panel>
          <Integrations isOnBoarding onIntegrationSelect={onIntegrationSelect} />
          <div className={styles('button-wrapper')}>
            <Button
              onClick={skipStep}
              type="submit"
              className={styles('button')}
              label={t(i18nKeys.ONBOARDING[5].CONFIGURATION_DONE)}
            />
          </div>
        </Panel>
      </div>
    </div>
  );
}
